import type { JSX } from 'react';
import type { CategoryDef, DashboardPayload, TasteDef } from '../../dashboard-types.js';
import { cardClassFor, deltaClass, deltaText, type Severity } from '../utils.js';

interface Props {
  data: DashboardPayload;
  sortedCategories: CategoryDef[];
  passesFilter: (c: CategoryDef) => boolean;
  selectedSizes: Set<string>;
  selectedTasteIds: Set<TasteDef['id']>;
  categoryTotal: (c: CategoryDef) => number;
  categoryDelta: (c: CategoryDef) => number;
  sizeTotal: (c: CategoryDef, sz: string) => number;
  sizeDelta: (c: CategoryDef, sz: string) => number;
  tasteCount: (c: CategoryDef, sz: string, tid: TasteDef['id']) => number;
  categoryWarnCountSizeTaste: (c: CategoryDef) => number;
  severityClass: (v: number) => Severity;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business カテゴリ × サイズ × テイスト タブ。各カテゴリ 1 カード内にサイズ行を並べ、各サイズ行の中にコンサバ/フェミニン/クール/カジュアル のテイスト別セルをグリッド表示
 * @graph-connects none
 */
export function CardsCategorySizeTaste(props: Props): JSX.Element {
  const list = props.sortedCategories.filter(props.passesFilter);
  if (list.length === 0) {
    return (
      <div className="cards">
        <p style={{ padding: 20, color: 'var(--text-muted)' }}>該当するカテゴリがありません</p>
      </div>
    );
  }
  return (
    <div className="cards">
      {list.map((c) => {
        const total = props.categoryTotal(c);
        const delta = props.categoryDelta(c);
        const sev = props.severityClass(total);
        const visibleSizes = props.data.sizes.filter((sz) => props.selectedSizes.has(sz));
        const visibleTastes = props.data.tastes.filter((t) => props.selectedTasteIds.has(t.id));
        return (
          <div key={c.id} className={`card ${cardClassFor(sev)}`}>
            <div className="card-head">
              <div className="card-head-left">
                <div className={`cat-icon ${c.cls}`}>{c.short}</div>
                <div className="cat-meta">
                  <span className="cat-name">{c.name}</span>
                  <span className="cat-id">category_id: {c.id}</span>
                </div>
              </div>
              <div className="card-summary">
                <span>
                  <b>{total.toLocaleString()}</b>
                  <small> 型</small>
                </span>
                <span className={`delta ${deltaClass(delta)}`}>{deltaText(delta)}</span>
              </div>
            </div>
            <div className="row-list">
              {visibleSizes.map((sz) => {
                const sizeV = props.sizeTotal(c, sz);
                const sizeD = props.sizeDelta(c, sz);
                const rowSev = props.severityClass(sizeV);
                return (
                  <div key={sz} className={`pat-row with-taste ${rowSev}`}>
                    <span className="size-badge">{sz}</span>
                    <div className="pat-bar-wrap">
                      <div className="pat-bar-meta">
                        <span>
                          <b style={{ color: 'var(--text)', fontWeight: 700 }}>
                            {sizeV.toLocaleString()}型
                          </b>{' '}
                          （size合計）
                        </span>
                        <span
                          className={`delta ${deltaClass(sizeD)}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
                        >
                          {deltaText(sizeD)}
                        </span>
                      </div>
                      <div className="taste-grid">
                        {visibleTastes.length === 0 ? (
                          <div
                            style={{
                              padding: 8,
                              color: 'var(--text-subtle)',
                              fontSize: 11,
                              gridColumn: '1/-1',
                            }}
                          >
                            該当テイストが選択されていません
                          </div>
                        ) : (
                          visibleTastes.map((t) => {
                            const v = props.tasteCount(c, sz, t.id);
                            const tSev = props.severityClass(v);
                            return (
                              <div key={t.id} className={`taste-cell ${tSev}`}>
                                <div className="t-name">
                                  <span>
                                    <span className={`t-tag ${t.cls}`}></span>
                                    {t.name}
                                  </span>
                                </div>
                                <div className="t-count">
                                  {v.toLocaleString()}
                                  <small>型</small>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                    <div className="pat-count">
                      <b>{sizeV.toLocaleString()}</b>
                      <small>型</small>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="card-foot">
              <span className="threshold-info">
                テイスト: コンサバ / フェミニン / クール / カジュアル
              </span>
              <span>
                閾値割れ: {props.categoryWarnCountSizeTaste(c)}件 /{' '}
                {props.data.sizes.length * props.data.tastes.length}スロット
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
